import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {specificationService} from '@/services/specificationService';
import type {
  CreateSpecificationParams,
  CreateSpecificationValueParams,
  Specification,
  SpecificationQueryParams,
  UpdateSpecificationParams,
  UpdateSpecificationValueParams,
} from '@/types/specification';

interface SpecificationState {
  items: Specification[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
}

const initialState: SpecificationState = {
  items: [],
  total: 0,
  page: 1,
  pageSize: 10,
  loading: false,
  error: null,
};

// 获取规格列表
export const fetchSpecifications = createAsyncThunk(
  'specification/fetchSpecifications',
  async (params: SpecificationQueryParams) => {
    const response = await specificationService.getSpecificationList(params);
    return response;
  }
);

// 创建规格
export const createSpecification = createAsyncThunk(
  'specification/createSpecification',
  async (data: CreateSpecificationParams) => {
    const response = await specificationService.createSpecification(data);
    return response;
  }
);

// 更新规格
export const updateSpecification = createAsyncThunk(
  'specification/updateSpecification',
  async ({ id, data }: { id: number; data: UpdateSpecificationParams }) => {
    const response = await specificationService.updateSpecification(id,data);
    return response;
  }
);

// 删除规格
export const deleteSpecification = createAsyncThunk(
  'specification/deleteSpecification',
  async (id: number) => {
    await specificationService.deleteSpecification(id);
    return id;
  }
);

// 创建规格值
export const createSpecificationValue = createAsyncThunk(
  'specification/createSpecificationValue',
  async ({ specificationId, data }: { specificationId: number; data: CreateSpecificationValueParams }) => {
    const response = await specificationService.createSpecificationValue(specificationId, data);
    return response;
  }
);

// 更新规格值
export const updateSpecificationValue = createAsyncThunk(
  'specification/updateSpecificationValue',
  async ({ specificationId, valueId, data }: { specificationId: number; valueId: number; data: UpdateSpecificationValueParams }) => {
    const response = await specificationService.updateSpecificationValue(specificationId, valueId, data);
    return response;
  }
);

// 删除规格值
export const deleteSpecificationValue = createAsyncThunk(
  'specification/deleteSpecificationValue',
  async ({ specificationId, valueId }: { specificationId: number; valueId: number }) => {
    const response = await specificationService.deleteSpecificationValue(specificationId, valueId);
    return response;
  }
);

// 更新规格状态
export const updateSpecificationStatus = createAsyncThunk(
  'specification/updateSpecificationStatus',
  async ({ id, enabled }: { id: number; enabled: boolean }) => {
    await specificationService.updateSpecificationStatus(id, enabled);
    return { id, enabled };
  }
);

// 更新规格值状态
export const updateSpecificationValueStatus = createAsyncThunk(
  'specification/updateSpecificationValueStatus',
  async ({ specificationId, valueId, enabled }: { specificationId: number; valueId: number; enabled: boolean }) => {
    await specificationService.updateSpecificationValueStatus(specificationId, valueId, enabled);
    return { specificationId, valueId, enabled };
  }
);

const specificationSlice = createSlice({
  name: 'specification',
  initialState,
  reducers: {
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // 获取规格列表
      .addCase(fetchSpecifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSpecifications.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.total = action.payload.total;
      })
      .addCase(fetchSpecifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '获取规格列表失败';
      })
      // 创建规格
      .addCase(createSpecification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSpecification.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload);
        state.total += 1;
      })
      .addCase(createSpecification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '创建规格失败';
      })
      // 更新规格
      .addCase(updateSpecification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSpecification.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateSpecification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '更新规格失败';
      })
      // 删除规格
      .addCase(deleteSpecification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSpecification.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter((item) => item.id !== action.payload);
        state.total -= 1;
      })
      .addCase(deleteSpecification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '删除规格失败';
      })
      // 创建规格值
      .addCase(createSpecificationValue.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSpecificationValue.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(createSpecificationValue.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '创建规格值失败';
      })
      // 更新规格值
      .addCase(updateSpecificationValue.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSpecificationValue.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateSpecificationValue.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '更新规格值失败';
      })
      // 删除规格值
      .addCase(deleteSpecificationValue.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSpecificationValue.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(deleteSpecificationValue.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '删除规格值失败';
      })
      // 更新规格状态
      .addCase(updateSpecificationStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSpecificationStatus.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index].enabled = action.payload.enabled;
        }
      })
      .addCase(updateSpecificationStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '更新规格状态失败';
      })
      // 更新规格值状态
      .addCase(updateSpecificationValueStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSpecificationValueStatus.fulfilled, (state, action) => {
        state.loading = false;
        const specification = state.items.find((item) => item.id === action.payload.specificationId);
        if (specification) {
          const valueIndex = specification.values.findIndex((value) => value.id === action.payload.valueId);
          if (valueIndex !== -1) {
            specification.values[valueIndex].enabled = action.payload.enabled;
          }
        }
      })
      .addCase(updateSpecificationValueStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '更新规格值状态失败';
      });
  },
});

export const { setPage, setPageSize, clearError } = specificationSlice.actions;
export default specificationSlice.reducer;
